import React, { useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Checkbox, Col, Form, InputNumber, Row, Select, Space, Spin, Typography } from "antd";
import { DownloadOutlined, EditOutlined, PictureOutlined, SearchOutlined } from "@ant-design/icons";
import { FaSave } from "react-icons/fa";
import HighchartsReact from "highcharts-react-official";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Highcharts from "../../Graficos/gerador/highcharts";
import { construirOpcoes } from "../../Graficos/gerador/modelos";
import { ALTURA_SAIDA, LARGURA_SAIDA } from "../../Graficos/gerador/tema";
import { baixarJpeg, gerarJpeg, montarHtmlImagem, salvarNoBanco } from "../../Graficos/gerador/exportar";
import { CHAVE_SPEC_IMPORTADO } from "../../Graficos/GeradorGraficos";
import { request } from "../../../functions/request";
import { DIMENSOES_RANKING, montarRankingComex } from "./montarRankingComex";

const { Text } = Typography;

/**
 * Ranking de exportação/importação do Comex, no formato de imagem da casa.
 *
 * Usa os mesmos filtros já escolhidos na tela (período, fluxo, commodities,
 * país, UF, bloco, URF) e soma o período inteiro no banco, agrupando por
 * estado, país ou URF. O desenho é um spec do Gerador de Gráficos, então a
 * imagem, o envio ao banco e a inserção na matéria são os mesmos de lá.
 */
export default function PainelRankingComex({ filtros, ehPopup }) {
  const navigate = useNavigate();
  const chartRef = useRef(null);

  const [dimensao, setDimensao] = useState("uf");
  const [top, setTop] = useState(5);
  const [mostrarTotal, setMostrarTotal] = useState(true);
  const [itens, setItens] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState(null);

  // Em R$, o FOB de cada item é o que a API já converteu com o dólar médio
  // de cada mês (total_fob_brl) — e a ordem passa a ser a dos reais.
  const emReais =
    filtros.moeda === "brl" && !["quilograma_liquido", "quantidade_estatistica"].includes(filtros.metrica);
  const linhasNaMoeda = useMemo(() => {
    if (!itens) return null;
    if (!emReais) return itens.linhas;
    return itens.linhas
      .map((l) => ({ ...l, total_fob_usd: l.total_fob_brl }))
      .sort((a, b) => b.total_fob_usd - a.total_fob_usd);
  }, [itens, emReais]);
  const mesesSemDolar = emReais && itens ? itens.linhas.some((l) => l.registros_sem_dolar > 0) : false;

  const spec = useMemo(
    () =>
      itens
        ? montarRankingComex(linhasNaMoeda, {
            dimensao: itens.dimensao,
            fluxo: filtros.fluxo,
            produto: itens.produto,
            metrica: filtros.metrica,
            moeda: filtros.moeda,
            unidadeQtd: filtros.unidadeQtd,
            anoInicio: itens.anoInicio,
            anoFim: itens.anoFim,
            meses: itens.meses,
            top,
            mostrarTotal,
          })
        : null,
    [itens, linhasNaMoeda, filtros.fluxo, filtros.metrica, filtros.moeda, filtros.unidadeQtd, top, mostrarTotal]
  );

  const opcoes = useMemo(() => {
    if (!spec) return null;
    try {
      return construirOpcoes(spec);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [spec]);

  const buscar = async () => {
    setBuscando(true);
    setErro(null);
    try {
      const base = new URLSearchParams();
      base.append("dimensao", dimensao);
      base.append("ano_inicio", filtros.anoInicio);
      base.append("ano_fim", filtros.anoFim);
      if (filtros.fluxo) base.append("fluxo", filtros.fluxo);
      filtros.meses.forEach((m) => base.append("meses", m));
      filtros.paises.forEach((p) => base.append("paises", p));
      filtros.ufs.forEach((u) => base.append("uf", u));
      filtros.urfs.forEach((u) => base.append("urf", u));
      [].concat(filtros.bloco || []).forEach((b) => base.append("bloco", b));
      [].concat(filtros.continentes || []).forEach((c) => base.append("continente", c));
      if (filtros.regiao) base.append("regiao", filtros.regiao);

      // Uma consulta por commodity (a API aceita uma por vez) e soma por item:
      // o ranking de "Soja + Milho" é o dos dois juntos.
      const commodities = filtros.commodities.length ? filtros.commodities : [null];
      const respostas = await Promise.all(
        commodities.map((commodity) => {
          const p = new URLSearchParams(base);
          if (commodity) {
            p.append("commodity", commodity);
            filtros.tiposDaCommodity(commodity).forEach((t) => p.append("tipo", t));
          }
          return request(`/comex-consulta/ranking?${p.toString()}`, { method: "GET" });
        })
      );

      const somados = new Map();
      for (const linhas of respostas) {
        for (const l of linhas || []) {
          const atual = somados.get(l.dimensao) || {
            dimensao: l.dimensao,
            total_fob_usd: 0,
            total_fob_brl: 0,
            registros_sem_dolar: 0,
            total_kg_liquido: 0,
            total_quantidade: 0,
          };
          atual.total_fob_usd += l.total_fob_usd || 0;
          atual.total_fob_brl += l.total_fob_brl || 0;
          atual.registros_sem_dolar += l.registros_sem_dolar || 0;
          atual.total_kg_liquido += l.total_kg_liquido || 0;
          atual.total_quantidade += l.total_quantidade || 0;
          somados.set(l.dimensao, atual);
        }
      }
      const linhas = [...somados.values()].sort((a, b) =>
        filtros.metrica === "quilograma_liquido"
          ? b.total_kg_liquido - a.total_kg_liquido
          : filtros.metrica === "quantidade_estatistica"
          ? b.total_quantidade - a.total_quantidade
          : b.total_fob_usd - a.total_fob_usd
      );

      if (!linhas.length) {
        toast.warning("Nenhum dado encontrado para os filtros selecionados.");
        setItens(null);
        return;
      }
      setItens({
        linhas,
        dimensao,
        produto: filtros.produto,
        anoInicio: filtros.anoInicio,
        anoFim: filtros.anoFim,
        meses: filtros.meses,
      });
    } catch (e) {
      setErro(e.message || "Erro ao buscar o ranking.");
    } finally {
      setBuscando(false);
    }
  };

  const legenda = spec ? spec.titulo.map((s) => s.texto).join("").trim() : "";

  const gerar = async (acao) => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    try {
      const blob = await gerarJpeg(chart);
      if (acao === "baixar") return baixarJpeg(blob, legenda || "ranking_comex");
      const { url } = await salvarNoBanco(blob, { legenda, comentario: `Comex ranking ${dimensao}` });
      if (acao === "inserir") {
        await navigator.clipboard.writeText(montarHtmlImagem(url, legenda));
        toast.success(ehPopup ? "Gráfico enviado para a matéria!" : "Código copiado — cole na matéria.");
      } else {
        toast.success("Gráfico salvo no banco de imagens (editoria Gráficos).");
      }
    } catch (e) {
      toast.error(e.message || "Não foi possível gerar a imagem.");
    }
  };

  const editarNoGerador = () => {
    try {
      sessionStorage.setItem(CHAVE_SPEC_IMPORTADO, JSON.stringify(spec));
    } catch {}
    navigate(`/graficos/gerador${ehPopup ? "?popup=1" : ""}`);
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={7} xl={6}>
        <Card title="Ranking" size="small">
          <Form layout="vertical" size="small">
            <Form.Item label="Agrupar por">
              <Select value={dimensao} onChange={setDimensao} options={DIMENSOES_RANKING} />
            </Form.Item>
            <Form.Item label="Itens no gráfico">
              <InputNumber min={3} max={10} value={top} onChange={(v) => setTop(v || 5)} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item>
              <Checkbox checked={mostrarTotal} onChange={(e) => setMostrarTotal(e.target.checked)}>
                Caixa com o total do período
              </Checkbox>
            </Form.Item>
            <Button type="primary" block icon={<SearchOutlined />} loading={buscando} onClick={buscar}>
              Gerar ranking
            </Button>
            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 8 }}>
              Usa os filtros ao lado (período, fluxo, commodities, país, UF, bloco e URF). O total da caixa é o do
              período inteiro, não só dos itens exibidos.
            </Text>
          </Form>
        </Card>
      </Col>

      <Col xs={24} lg={17} xl={18}>
        <Card size="small">
          {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 12 }} />}
          {mesesSemDolar && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Parte do período não tem cotação do dólar (a tabela começa em 2000): esses meses ficam fora dos valores em R$."
            />
          )}
          {buscando ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: ALTURA_SAIDA }}>
              <Spin size="large" />
            </div>
          ) : opcoes ? (
            <>
              <div style={{ background: "#2b2b2b", padding: 10, borderRadius: 4, overflowX: "auto" }}>
                <div style={{ width: LARGURA_SAIDA, height: ALTURA_SAIDA, margin: "0 auto" }}>
                  <HighchartsReact key={`${itens.dimensao}-${top}`} ref={chartRef} highcharts={Highcharts} options={opcoes} />
                </div>
              </div>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
                {itens.linhas.length}{" "}
                {{ uf: "estados", regiao: "regiões", pais: "países" }[itens.dimensao] || "URFs"} no período.
                {["valor_medio_ton", "valor_medio_qtd"].includes(filtros.metrica) &&
                  " Valor médio não tem total somável, então a caixa fica de fora."}
              </Text>
              <Space wrap style={{ marginTop: 12 }}>
                <Button type="primary" icon={<PictureOutlined />} onClick={() => gerar("inserir")}>
                  {ehPopup ? "Inserir na matéria" : "Gerar e copiar"}
                </Button>
                <Button icon={<FaSave />} onClick={() => gerar("salvar")}>
                  Salvar no banco de imagens
                </Button>
                <Button icon={<DownloadOutlined />} onClick={() => gerar("baixar")}>
                  Baixar JPG
                </Button>
                <Button icon={<EditOutlined />} onClick={editarNoGerador}>
                  Editar no Gerador
                </Button>
              </Space>
            </>
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
              <Typography.Title level={4} type="secondary" style={{ textAlign: "center" }}>
                Ajuste os filtros e clique em "Gerar ranking"
              </Typography.Title>
            </div>
          )}
        </Card>
      </Col>
    </Row>
  );
}
